"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { pathCellRevealDelayMs } from "../animations";

interface UseHexagonMapAnimationsOptions {
	layoutSeed: string;
	pathLength: number;
	pathTileDelayMs?: number;
	initialGridAnimationLeadDelayMs?: number;
	animationEnabled?: boolean;
	reducedMotion?: boolean;
}

export function useHexagonMapAnimations(
	options: UseHexagonMapAnimationsOptions,
) {
	const {
		layoutSeed,
		pathLength,
		pathTileDelayMs,
		initialGridAnimationLeadDelayMs = 0,
		animationEnabled = true,
		reducedMotion,
	} = options;
	const hasPlayedInitialPathLeadDelayRef = useRef(reducedMotion ?? false);
	const [revealedThroughIndex, setRevealedThroughIndex] = useState(
		reducedMotion ? Number.POSITIVE_INFINITY : -1,
	);

	// Intentional deps: reset path reveal state when layout seed identity changes.
	useEffect(() => {
		if (!animationEnabled) {
			hasPlayedInitialPathLeadDelayRef.current = reducedMotion ?? false;
			setRevealedThroughIndex(Number.POSITIVE_INFINITY);
			return;
		}
		if (reducedMotion || pathLength <= 0) {
			setRevealedThroughIndex(Number.POSITIVE_INFINITY);
			return;
		}

		setRevealedThroughIndex(-1);
		const timers: ReturnType<typeof setTimeout>[] = [];
		const pathRevealLeadDelayMs = hasPlayedInitialPathLeadDelayRef.current
			? 0
			: initialGridAnimationLeadDelayMs;
		hasPlayedInitialPathLeadDelayRef.current = true;

		for (let i = 0; i < pathLength; i++) {
			const delay =
				pathRevealLeadDelayMs +
				pathCellRevealDelayMs(i, { pathTileDelayMs });
			timers.push(
				setTimeout(() => {
					setRevealedThroughIndex((prev) => Math.max(prev, i));
				}, delay),
			);
		}

		return () => {
			for (const timer of timers) {
				clearTimeout(timer);
			}
		};
	}, [
		layoutSeed,
		pathLength,
		pathTileDelayMs,
		reducedMotion,
		initialGridAnimationLeadDelayMs,
		animationEnabled,
	]);

	const isPathIndexVisible = useMemo(
		() => (pathIndex: number) => pathIndex <= revealedThroughIndex,
		[revealedThroughIndex],
	);

	const isPathRevealComplete = useMemo(
		() => pathLength <= 0 || revealedThroughIndex >= pathLength - 1,
		[pathLength, revealedThroughIndex],
	);

	return {
		isPathIndexVisible,
		isPathRevealComplete,
		revealedThroughIndex,
	};
}
